import { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { api } from './api';
import { fmtDate, weekStart, weekEnd, dayLabel } from './dates';

// ── PDF builders ─────────────────────────────────────────────────────────────

// Build the entries table HTML that will be substituted into the template.
function buildEntriesTableHtml(report) {
  const groupRows = report.project_totals.map(pt => {
    const entries = report.entries.filter(e => e.project_id === pt.project_id);
    const feeNote = pt.platform_fee_pct > 0
      ? `$${Math.round(pt.rate_per_hour)}/hr · ${pt.platform_fee_pct}% platform fee → $${Math.round(pt.net_rate)}/hr`
      : `$${Math.round(pt.rate_per_hour)}/hr · no platform fee`;

    const detailRows = entries.map(e => `
      <tr>
        <td>${dayLabel(e.entry_date)}</td>
        <td>${e.entry_date}</td>
        <td class="desc">${escapeHtml(e.description)}${e.invoice_number ? ` <span class="grp-meta">[${escapeHtml(e.invoice_number)}]</span>` : ''}</td>
        <td class="num">${Number(e.hours).toFixed(1)}</td>
        <td class="num">$${e.amount.toLocaleString()}</td>
      </tr>`).join('');

    return `
      <tr class="grp-head">
        <td colspan="5">${escapeHtml(pt.project_name)} <span class="grp-meta">${feeNote}</span></td>
      </tr>
      ${detailRows}
      <tr class="grp-total">
        <td colspan="3" class="right">${escapeHtml(pt.project_name)} total</td>
        <td class="num">${Number(pt.total_hours).toFixed(1)}</td>
        <td class="num">$${pt.total_amount.toLocaleString()}</td>
      </tr>`;
  }).join('');

  return `
    <table>
      <thead>
        <tr>
          <th style="width:60px">Day</th>
          <th style="width:100px">Date</th>
          <th>Description</th>
          <th style="width:55px" class="num">Hours</th>
          <th style="width:75px" class="num">Amount</th>
        </tr>
      </thead>
      <tbody>${groupRows}</tbody>
      <tfoot>
        <tr class="grand">
          <td colspan="3" class="right" style="color:#555;font-weight:400;">Grand total — hours</td>
          <td class="num">${Number(report.grand_total_hours).toFixed(1)}</td>
          <td></td>
        </tr>
        <tr class="grand" style="border-top:none">
          <td colspan="3" class="right" style="color:#555;font-weight:400;">Grand total — total billed</td>
          <td></td>
          <td class="num green">$${report.grand_total_amount.toLocaleString()}</td>
        </tr>
      </tfoot>
    </table>`;
}

// Build the pre-paid status HTML (only for non-platform projects)
function buildPrepaidHtml(invoices) {
  if (!invoices || invoices.length === 0) return '';

  const rows = invoices.map(inv => {
    const pct  = Math.min(100, Number(inv.pct_used) || 0);
    const cls  = pct >= 100 ? 'full' : pct >= 80 ? 'warn' : '';
    return `
      <div class="prepaid-row">
        <span><strong>${escapeHtml(inv.invoice_number)}</strong> · ${escapeHtml(inv.project_name)}</span>
        <span>
          ${Number(inv.hours_used).toFixed(1)} / ${Number(inv.hours_purchased).toFixed(0)} hrs used
          <span class="prepaid-bar"><span class="prepaid-bar-fill ${cls}" style="width:${pct}%;"></span></span>
          ${Number(inv.hours_remaining).toFixed(1)} hrs left
        </span>
      </div>`;
  }).join('');

  return `
    <h2 class="section">Pre-paid hours status</h2>
    ${rows}`;
}

// Build the open items "Next Week" section
function buildOpenItemsHtml(items) {
  if (!items || items.length === 0) return '';

  const lis = items.map(i => `<li>${escapeHtml(i.description)}</li>`).join('');
  return `
    <h2 class="section">Next week</h2>
    <ul class="next-week">${lis}</ul>`;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

// Substitute {{PLACEHOLDER}} tokens in the template
function renderTemplate(template, values) {
  return Object.entries(values).reduce(
    (html, [key, val]) => html.replaceAll(`{{${key}}}`, val ?? ''),
    template
  );
}

// Hard-coded sender contact info (matches what you asked for).
// If you ever want this editable, move these into the template or a settings row.
const SENDER = {
  SENDER_NAME:           'Mike Bennyhoff',
  SENDER_COMPANY:        'Bennyhoff Products And Services LLC',
  SENDER_ADDRESS:        '1825 Beverly Way',
  SENDER_CITY_STATE_ZIP: 'Sacramento, CA 95818',
  SENDER_PHONE:          '916-303-3627',
  SENDER_EMAIL:          'Mike@BPS-Corp.com',
};

async function exportPdf({ report, from, to, projectId, projects }) {
  // Fetch template + supporting data in parallel
  const [tpl, invoicesAll, openItems] = await Promise.all([
    api.getPdfTemplate(),
    api.getInvoices(projectId || undefined, false),
    projectId
      ? api.getOpenItems(projectId, true)   // only open items
      : Promise.resolve([]),                // don't show open items when all projects
  ]);

  const clientName = projectId
    ? (projects.find(p => String(p.id) === String(projectId))?.name || 'Client')
    : 'All Clients';

  // Filter invoices to non-platform projects only (platform projects don't use invoices)
  const nonPlatformProjectIds = new Set(
    projects.filter(p => !p.is_platform_work).map(p => p.id)
  );
  const prepaidInvoices = invoicesAll.filter(i => nonPlatformProjectIds.has(i.project_id));

  const html = renderTemplate(tpl.html_body, {
    ...SENDER,
    TITLE:              'Status Update / Invoice',
    CLIENT_NAME:        escapeHtml(clientName),
    DATE_RANGE:         `${from} → ${to}`,
    ENTRIES_TABLE_HTML: buildEntriesTableHtml(report),
    PREPAID_STATUS_HTML: buildPrepaidHtml(prepaidInvoices),
    OPEN_ITEMS_HTML:    buildOpenItemsHtml(openItems),
    GRAND_TOTAL_HOURS:  Number(report.grand_total_hours).toFixed(1),
    GRAND_TOTAL_BILLED: `$${report.grand_total_amount.toLocaleString()}`,
  });

  // Sanitize before writing. The template body (tpl.html_body) is admin-editable,
  // so a compromised or malicious template could otherwise land a <script> in the
  // print window and steal the session. DOMPurify strips <script>, event handlers,
  // and javascript: URLs while keeping <style>, classes, and inline style= needed
  // for PDF rendering. WHOLE_DOCUMENT preserves <html>/<head>/<body> structure.
  const safeHtml = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    FORBID_TAGS:    ['script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR:    ['srcdoc', 'formaction'],
  });

  const win = window.open('', '_blank');
  win.document.write(safeHtml);
  win.document.close();
  win.onload = () => { win.focus(); win.print(); };
}

function exportCsv(report, from, to) {
  // Encode a single cell:
  //  - Neutralize CSV-injection by prefixing any cell whose first character is
  //    a formula trigger (= + - @ TAB CR) with a single quote. Excel/Sheets
  //    would otherwise interpret it as a formula and could open external links
  //    or exfiltrate clipboard contents when the user opens the file.
  //  - Always wrap in quotes and double embedded quotes, so values containing
  //    commas, quotes, or newlines can't malform the CSV.
  const cell = (v) => {
    if (v === null || v === undefined) return '""';
    const s = String(v);
    const needsPrefix = /^[=+\-@\t\r]/.test(s);
    const escaped = (needsPrefix ? "'" : '') + s.replace(/"/g, '""');
    return `"${escaped}"`;
  };

  const rows = [
    ['Date', 'Day', 'Project', 'Invoice #', 'Description', 'Hours', 'Amount'].map(cell),
    ...report.entries.map(e => [
      cell(e.entry_date),
      cell(dayLabel(e.entry_date)),
      cell(e.project_name),
      cell(e.invoice_number || ''),
      cell(e.description),
      cell(e.hours),
      cell(e.amount),
    ]),
    [],
    ['', '', '', '', 'TOTAL HOURS',  report.grand_total_hours,  ''].map(cell),
    ['', '', '', '', 'TOTAL BILLED', '',                         report.grand_total_amount].map(cell),
  ];
  const csv  = rows.map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `time-report-${from}-to-${to}.csv`; a.click();
  URL.revokeObjectURL(url);
}

// ── Outlook email body ─────────────────────────────────────────────────────
// Email clients strip <style>/classes, so the report email is built with INLINE
// styles only. Mirrors the PDF content (per-project groups + totals) but as a
// self-contained HTML fragment Graph drops into the draft body.
function buildEmailHtml({ report, from, to, clientName }) {
  const money = n => `$${Number(n).toLocaleString()}`;
  const td = (s, extra = '') => `<td style="padding:6px 8px;border-bottom:1px solid #e3e6ee;${extra}">${s}</td>`;

  const groups = report.project_totals.map(pt => {
    const rows = report.entries.filter(e => e.project_id === pt.project_id).map(e => `
      <tr>
        ${td(e.entry_date, 'white-space:nowrap;color:#6b7185;')}
        ${td(escapeHtml(e.description))}
        ${td(Number(e.hours).toFixed(1), 'text-align:right;white-space:nowrap;')}
        ${td(money(e.amount), 'text-align:right;white-space:nowrap;')}
      </tr>`).join('');
    return `
      <tr><td colspan="4" style="padding:10px 8px 4px;font-weight:600;color:#1f2330;">${escapeHtml(pt.project_name)}</td></tr>
      ${rows}
      <tr>
        <td colspan="2" style="padding:6px 8px;text-align:right;color:#6b7185;border-top:1px solid #e3e6ee;">${escapeHtml(pt.project_name)} total</td>
        <td style="padding:6px 8px;text-align:right;font-weight:600;border-top:1px solid #e3e6ee;">${Number(pt.total_hours).toFixed(1)}</td>
        <td style="padding:6px 8px;text-align:right;font-weight:600;border-top:1px solid #e3e6ee;">${money(pt.total_amount)}</td>
      </tr>`;
  }).join('');

  return `
  <div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#1f2330;line-height:1.5;">
    <p>Hi,</p>
    <p>Please find the status update / invoice for <strong>${escapeHtml(clientName)}</strong> covering <strong>${from} → ${to}</strong>.</p>
    <table style="border-collapse:collapse;width:100%;max-width:640px;font-size:13px;margin:12px 0;">
      <thead>
        <tr style="background:#f1f3f9;">
          <th style="padding:6px 8px;text-align:left;color:#6b7185;">Date</th>
          <th style="padding:6px 8px;text-align:left;color:#6b7185;">Description</th>
          <th style="padding:6px 8px;text-align:right;color:#6b7185;">Hours</th>
          <th style="padding:6px 8px;text-align:right;color:#6b7185;">Amount</th>
        </tr>
      </thead>
      <tbody>${groups}</tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="padding:8px;text-align:right;font-weight:600;border-top:2px solid #d4d8e3;">Grand total</td>
          <td style="padding:8px;text-align:right;font-weight:700;border-top:2px solid #d4d8e3;">${Number(report.grand_total_hours).toFixed(1)}</td>
          <td style="padding:8px;text-align:right;font-weight:700;color:#1f8a3e;border-top:2px solid #d4d8e3;">${money(report.grand_total_amount)}</td>
        </tr>
      </tfoot>
    </table>
    <p>Thank you,</p>
    <p style="margin:0;">
      <strong>${SENDER.SENDER_NAME}</strong><br/>
      ${SENDER.SENDER_COMPANY}<br/>
      ${SENDER.SENDER_PHONE} · ${SENDER.SENDER_EMAIL}
    </p>
  </div>`;
}

// ── Email-to-client dialog ──────────────────────────────────────────────────
// Creates an Outlook draft in the signed-in user's OWN mailbox via the backend,
// then tells them to review and Send from Outlook (we never send for them).
function EmailDialog({ report, from, to, clientName, onClose }) {
  const [to_, setTo]         = useState('');
  const [subject, setSubject] = useState(`Status Update / Invoice — ${clientName} (${from} → ${to})`);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState(null);

  async function createDraft() {
    setBusy(true); setError('');
    try {
      const recipients = to_.split(',').map(s => s.trim()).filter(Boolean);
      const res = await api.createMailDraft({
        to:        recipients,
        subject,
        body_html: buildEmailHtml({ report, from, to, clientName }),
      });
      setResult(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <div className="modal-title">Email to client</div>
            <div className="modal-sub">Creates a draft in your Outlook — you review and Send.</div>
          </div>
          <button className="modal-x" onClick={onClose}>×</button>
        </div>

        {!result ? (
          <>
            <div className="field" style={{ marginBottom: 12 }}>
              <label>To</label>
              <input value={to_} onChange={e => setTo(e.target.value)} placeholder="client@example.com, cc@example.com" />
              <span className="hint">Comma-separated. Leave blank to fill in inside Outlook.</span>
            </div>
            <div className="field" style={{ marginBottom: 12 }}>
              <label>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} />
            </div>
            <div className="info-note">
              The report ({from} → {to}) is included in the email body. After you click
              <strong> Create draft</strong>, open <strong>Outlook → Drafts</strong>, review it, and click <strong>Send</strong>.
              Nothing is sent automatically.
            </div>
            {error && <p className="error-msg">{error}</p>}
            <div className="modal-footer">
              <button className="btn-outline" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={createDraft} disabled={busy}>
                {busy ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="info-note" style={{ background: '#eafaf0', color: '#1f8a3e' }}>
              ✓ Draft created in your Outlook{result.mailbox ? <> ({result.mailbox})</> : null}.
            </div>
            <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, margin: '12px 0' }}>
              It's in your <strong>Drafts</strong> folder. Open it in Outlook, review the content,
              edit if needed, and click <strong>Send</strong> — the email goes out from your own account.
            </p>
            <div className="modal-footer">
              {result.web_link && (
                <a className="btn-primary" href={result.web_link} target="_blank" rel="noopener"
                   style={{ textDecoration: 'none' }}>
                  Open in Outlook →
                </a>
              )}
              <button className="btn-outline" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export default function Report({ projects, canEmail = true }) {
  const [from,      setFrom]      = useState(fmtDate(weekStart()));
  const [to,        setTo]        = useState(fmtDate(weekEnd()));
  const [projectId, setProjectId] = useState('');
  const [report,    setReport]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const clientName = projectId
    ? (projects.find(p => String(p.id) === String(projectId))?.name || 'Client')
    : 'All Clients';

  // Earnings summary strip — independent of the from/to/project filter.
  const [summary,      setSummary]      = useState(null);
  const [summaryScope, setSummaryScope] = useState('all');  // all | platform | direct

  async function load() {
    setLoading(true); setError('');
    try {
      setReport(await api.getReport(from, to, projectId || undefined));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadSummary() {
    try {
      setSummary(await api.getEarningsSummary(summaryScope));
    } catch {
      /* non-critical; leave the strip blank on failure */
    }
  }

  useEffect(() => { load(); }, [from, to, projectId]); // eslint-disable-line
  useEffect(() => { loadSummary(); }, [summaryScope]); // eslint-disable-line

  async function handleExportPdf() {
    setExporting(true); setError('');
    try {
      await exportPdf({ report, from, to, projectId, projects });
    } catch (err) {
      setError('PDF export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div>
      {/* ── Earnings summary strip ── */}
      <div style={{
        display: 'flex',
        gap: 8,
        alignItems: 'stretch',
        marginBottom: '1rem',
      }}>
        {['mtd', 'qtd', 'ytd'].map(key => {
          const s = summary && summary[key];
          const label = key === 'mtd' ? 'Month to date' : key === 'qtd' ? 'Quarter to date' : 'Year to date';
          return (
            <div key={key} style={{
              flex: 1,
              background: '#f9f9f8',
              borderRadius: 6,
              padding: '10px 12px',
              minWidth: 0,
            }}>
              <div style={{ fontSize: 10, color: '#737370', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 3, color: '#1a1a1a' }}>
                {s ? `$${s.amount.toLocaleString()}` : '—'}
              </div>
              <div style={{ fontSize: 11, color: '#737370', marginTop: 2 }}>
                {s ? `${Number(s.hours).toFixed(1)} hrs · from ${s.from}` : ''}
              </div>
            </div>
          );
        })}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          background: '#fff',
          border: '1px solid #e5e5e3',
          borderRadius: 6,
          padding: '8px 10px',
        }}>
          <div style={{ fontSize: 10, color: '#737370', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
            Scope
          </div>
          <select
            value={summaryScope}
            onChange={e => setSummaryScope(e.target.value)}
            style={{ fontSize: 13, padding: '4px 6px', border: '1px solid #e5e5e3', borderRadius: 4, background: '#fff' }}
          >
            <option value="all">All work</option>
            <option value="platform">Platform only</option>
            <option value="direct">Direct only</option>
          </select>
        </div>
      </div>

      {/* ── Controls ── */}
      <div className="report-controls">
        <div className="field">
          <label>From</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="field">
          <label>To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="field">
          <label>Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">All projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>&nbsp;</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn-outline" disabled={!report} onClick={() => exportCsv(report, from, to)}>↓ CSV</button>
            <button className="btn-outline" disabled={!report || exporting} onClick={handleExportPdf}>
              {exporting ? 'Preparing…' : '↓ PDF'}
            </button>
            {canEmail ? (
              <button className="btn-primary" disabled={!report || !report.entries.length} onClick={() => setEmailOpen(true)}>
                ✉ Email
              </button>
            ) : (
              <a className="btn-outline" href="https://bps-corp.com/Products/UpTracker#pricing" target="_blank" rel="noopener"
                 title="Emailing a client as an Outlook draft is a Pro feature" style={{ textDecoration: 'none' }}>
                🔒 Email (Pro)
              </a>
            )}
          </div>
        </div>
      </div>

      {emailOpen && report && (
        <EmailDialog
          report={report}
          from={from}
          to={to}
          clientName={clientName}
          onClose={() => setEmailOpen(false)}
        />
      )}

      {error   && <p className="error-msg">{error}</p>}
      {loading && <p style={{ color: '#737370', padding: '1rem 0' }}>Loading…</p>}
      {report && report.entries.length === 0 && (
        <p style={{ color: '#737370', padding: '1rem 0' }}>No entries found for this period.</p>
      )}

      {!projectId && report && report.entries.length > 0 && (
        <p className="hint" style={{ marginBottom: 10 }}>
          Tip: pick a single project above to include that project's <strong>open items</strong> in the PDF as "Next Week".
        </p>
      )}

      {report && report.entries.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 90 }}>Day</th>
                <th style={{ width: 115 }}>Date</th>
                <th>Description</th>
                <th style={{ width: 100 }}>Invoice</th>
                <th style={{ width: 60, textAlign: 'right' }}>Hours</th>
                <th style={{ width: 80, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            {report.project_totals.map(pt => {
              const ptRows = report.entries.filter(e => e.project_id === pt.project_id);
              return (
                <tbody key={pt.project_id}>
                  <tr className="group-header">
                    <td colSpan={6}>
                      {pt.project_name}&nbsp;
                      <span style={{ fontSize: 11, fontWeight: 400, color: '#737370' }}>
                        ${Math.round(pt.rate_per_hour)}/hr
                        {pt.platform_fee_pct > 0
                          ? ` · ${pt.platform_fee_pct}% fee → $${Math.round(pt.net_rate)}/hr`
                          : ' · no platform fee'}
                        {pt.is_platform_work && ' · platform work'}
                      </span>
                    </td>
                  </tr>
                  {ptRows.map(e => (
                    <tr key={e.id}>
                      <td><span className="day-badge">{dayLabel(e.entry_date)}</span></td>
                      <td className="date-col">{e.entry_date}</td>
                      <td className="desc-cell">{e.description}</td>
                      <td style={{ fontSize: 12, color: e.invoice_number ? '#1a1a1a' : '#a8a8a3' }}>
                        {e.is_platform_work
                          ? <span style={{ fontStyle: 'italic' }}>platform</span>
                          : (e.invoice_number || '—')}
                      </td>
                      <td style={{ textAlign: 'right' }}>{Number(e.hours).toFixed(1)}</td>
                      <td style={{ textAlign: 'right' }}>${e.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="group-total">
                    <td colSpan={4} style={{ textAlign: 'right', paddingRight: 14 }}>{pt.project_name} total</td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: '#1a1a1a' }}>{Number(pt.total_hours).toFixed(1)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500, color: '#1a1a1a' }}>${pt.total_amount.toLocaleString()}</td>
                  </tr>
                </tbody>
              );
            })}
            <tfoot>
              <tr className="grand-total">
                <td colSpan={4}>Grand total — hours</td>
                <td style={{ textAlign: 'right' }}>{Number(report.grand_total_hours).toFixed(1)}</td>
                <td></td>
              </tr>
              <tr className="grand-total" style={{ borderTop: 'none' }}>
                <td colSpan={4}>Grand total — earnings</td>
                <td></td>
                <td style={{ textAlign: 'right', color: '#16a34a' }}>${report.grand_total_amount.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
